import { useInitEmrSockets } from '@/entities/emr-integration';
import { useSessionStore } from '@/entities/session';
import { globalSocket } from '@/shared/api/sockets';
import { useInitSocket } from '@/shared/lib/hooks';
import { usePageTracking } from '@/shared/lib/tracking';
import { CurrentUserShortInfo, Header, ThemeToggle } from '@/widgets/header';
import { Sidebar } from '@/widgets/sidebar';
import { useRouter } from '@tanstack/react-router';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import { GlobalLayout } from './global.layout';

type PlatformLayoutProps = PropsWithChildren;

export const PlatformLayout: FC<PlatformLayoutProps> = ({ children }) => {
  const userId = useSessionStore((state) => state.user?.id);
  const accessToken = useSessionStore((state) => state.accessToken);
  const router = useRouter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useInitSocket(globalSocket, {
    id: 'global',
    userId,
    accessToken,
  });
  useInitEmrSockets();
  // The app scrolls this layout's inner container, not the window, so scroll
  // depth tracking needs that ref rather than defaulting to `window`.
  usePageTracking(scrollContainerRef);

  useEffect(() => {
    const unsubscribe = router.subscribe('onLoad', () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    });

    return () => unsubscribe();
  }, [router]);

  return (
    <GlobalLayout>
      <Header
        withFilters
        rightAddons={[
          <ThemeToggle key="theme-toggle" />,
          <CurrentUserShortInfo key="user-short-info" />,
        ]}
      />
      <div className="flex max-h-[calc(100vh-90px)]">
        <Sidebar />
        <div
          className="bg-neutral-2 min-w-0 flex-1 overflow-auto"
          ref={scrollContainerRef}
        >
          <div className="min-w-[900px]">{children}</div>
        </div>
      </div>
    </GlobalLayout>
  );
};
